import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { ArrowRight, Copy, Check, ChevronDown } from "lucide-react";
import { PRIMARY_SCHOOLS } from "@/data/schools";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function LandingPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState("intro"); // intro, create, success
  const [gradeLevel, setGradeLevel] = useState("");
  const [classIdentifier, setClassIdentifier] = useState("");
  const [school, setSchool] = useState("");
  const [schoolSearch, setSchoolSearch] = useState("");
  const [showSchoolDropdown, setShowSchoolDropdown] = useState(false);
  const [loading, setLoading] = useState(false);
  const [classData, setClassData] = useState(null);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState("");

  const handleCreateClass = async (e) => {
    e.preventDefault();
    if (!gradeLevel || !classIdentifier.trim() || !school || loading) return;

    setLoading(true);
    setError("");

    // Combine grade level and class identifier
    const fullClassName = `${gradeLevel}${classIdentifier.trim()}`;

    try {
      const response = await axios.post(`${API}/admin/classes`, {
        name: fullClassName,
        school: school,
        dismissal_time: "15:00",
        window_minutes: 90
      });
      setClassData(response.data);
      setStep("success");
    } catch (err) {
      console.error("Error creating class:", err);
      if (err.response?.status === 409) {
        setError("This class name already exists. Please choose a different name.");
      } else {
        setError("Failed to create class. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  // Filter schools based on search
  const filteredSchools = PRIMARY_SCHOOLS.filter(s =>
    s.toLowerCase().includes(schoolSearch.toLowerCase())
  );

  const handleSelectSchool = (selectedSchool) => {
    setSchool(selectedSchool);
    setSchoolSearch(selectedSchool);
    setShowSchoolDropdown(false);
  };

  const getInviteLink = () => {
    if (!classData) return "";
    return `${window.location.origin}/pulse/${classData.token}`;
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(getInviteLink());
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error("Copy failed:", err);
    }
  };

  const handleShareWhatsApp = () => {
    const link = getInviteLink();
    const text = `Join our class dismissal pulse: ${link}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank");
  };

  // Intro step
  if (step === "intro") {
    return (
      <div className="pulse-container" data-testid="landing-container">
        <div className="pulse-card">
          <div className="pulse-header">
            <h1 className="pulse-title" data-testid="landing-title">Dismissal Pulse</h1>
            <p className="pulse-subtitle">Parent reassurance, simplified</p>
          </div>

          <div className="neutral-message" style={{ padding: "1.5rem 0" }}>
            <p className="neutral-text" style={{ marginBottom: "1.5rem" }}>
              A calm, simple way for parents to share dismissal updates with their class.
            </p>
            <p className="neutral-text" style={{ fontSize: "0.875rem", color: "#9CA3AF" }}>
              No tracking. No accounts. Just reassurance.
            </p>
          </div>

          <button
            onClick={() => setStep("create")}
            data-testid="start-class-btn"
            className="primary-btn"
          >
            <span>Start for your class</span>
            <ArrowRight size={18} />
          </button>

          <p className="disclaimer" style={{ marginTop: "1.5rem" }}>
            Each class has its own private pulse.
          </p>
        </div>
      </div>
    );
  }

  // Create class step
  if (step === "create") {
    return (
      <div className="pulse-container" data-testid="create-container">
        <div className="pulse-card">
          <div className="pulse-header">
            <h1 className="pulse-title">Create Your Class Pulse</h1>
            <p className="pulse-subtitle">One pulse per class</p>
          </div>

          <form onSubmit={handleCreateClass} style={{ marginTop: "1.5rem" }}>
            <div style={{ marginBottom: "1rem" }}>
              <label
                htmlFor="gradeLevel"
                style={{
                  display: "block",
                  fontSize: "0.875rem",
                  color: "#6B7280",
                  marginBottom: "0.5rem"
                }}
              >
                Grade level
              </label>
              <select
                id="gradeLevel"
                value={gradeLevel}
                onChange={(e) => setGradeLevel(e.target.value)}
                data-testid="grade-level-select"
                style={{
                  width: "100%",
                  padding: "0.875rem 1rem",
                  borderRadius: "0.75rem",
                  border: "1px solid #E5E7EB",
                  fontSize: "1rem",
                  fontFamily: "'Work Sans', sans-serif",
                  outline: "none",
                  transition: "border-color 0.2s",
                  boxSizing: "border-box",
                  backgroundColor: "white"
                }}
              >
                <option value="">Select grade</option>
                <option value="1">Grade 1</option>
                <option value="2">Grade 2</option>
                <option value="3">Grade 3</option>
                <option value="4">Grade 4</option>
                <option value="5">Grade 5</option>
                <option value="6">Grade 6</option>
                <option value="7">Grade 7</option>
              </select>
            </div>

            <div style={{ marginBottom: "1rem" }}>
              <label
                htmlFor="classIdentifier"
                style={{
                  display: "block",
                  fontSize: "0.875rem",
                  color: "#6B7280",
                  marginBottom: "0.5rem"
                }}
              >
                Class name
              </label>
              <input
                id="classIdentifier"
                type="text"
                value={classIdentifier}
                onChange={(e) => setClassIdentifier(e.target.value)}
                placeholder="e.g., B, Orchid, Integrity"
                data-testid="class-identifier-input"
                style={{
                  width: "100%",
                  padding: "0.875rem 1rem",
                  borderRadius: "0.75rem",
                  border: "1px solid #E5E7EB",
                  fontSize: "1rem",
                  fontFamily: "'Work Sans', sans-serif",
                  outline: "none",
                  transition: "border-color 0.2s",
                  boxSizing: "border-box"
                }}
              />
            </div>

            <div style={{ marginBottom: "1rem", position: "relative" }}>
              <label
                htmlFor="school"
                style={{
                  display: "block",
                  fontSize: "0.875rem",
                  color: "#6B7280",
                  marginBottom: "0.5rem"
                }}
              >
                School
              </label>
              <div style={{ position: "relative" }}>
                <input
                  id="school"
                  type="text"
                  value={schoolSearch}
                  onChange={(e) => {
                    setSchoolSearch(e.target.value);
                    setSchool("");
                    setShowSchoolDropdown(true);
                  }}
                  onFocus={() => setShowSchoolDropdown(true)}
                  placeholder="Search for your school..."
                  data-testid="school-input"
                  style={{
                    width: "100%",
                    padding: "0.875rem 2.5rem 0.875rem 1rem",
                    borderRadius: "0.75rem",
                    border: "1px solid #E5E7EB",
                    fontSize: "1rem",
                    fontFamily: "'Work Sans', sans-serif",
                    outline: "none",
                    transition: "border-color 0.2s",
                    boxSizing: "border-box"
                  }}
                />
                <ChevronDown
                  size={20}
                  style={{
                    position: "absolute",
                    right: "1rem",
                    top: "50%",
                    transform: "translateY(-50%)",
                    color: "#6B7280",
                    pointerEvents: "none"
                  }}
                />
                {showSchoolDropdown && filteredSchools.length > 0 && (
                  <div
                    style={{
                      position: "absolute",
                      top: "100%",
                      left: 0,
                      right: 0,
                      marginTop: "0.25rem",
                      background: "white",
                      border: "1px solid #E5E7EB",
                      borderRadius: "0.75rem",
                      maxHeight: "200px",
                      overflowY: "auto",
                      zIndex: 10,
                      boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)"
                    }}
                  >
                    {filteredSchools.map((s, index) => (
                      <div
                        key={index}
                        onClick={() => handleSelectSchool(s)}
                        style={{
                          padding: "0.75rem 1rem",
                          cursor: "pointer",
                          fontSize: "0.9375rem",
                          color: "#374151",
                          borderBottom: index < filteredSchools.length - 1 ? "1px solid #F3F4F6" : "none",
                          transition: "background-color 0.15s",
                          fontFamily: "'Work Sans', sans-serif"
                        }}
                        onMouseEnter={(e) => e.target.style.backgroundColor = "#F9FAFB"}
                        onMouseLeave={(e) => e.target.style.backgroundColor = "transparent"}
                      >
                        {s}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {error && (
              <div
                data-testid="error-message"
                style={{
                  marginBottom: "1rem",
                  padding: "0.75rem 1rem",
                  borderRadius: "0.75rem",
                  backgroundColor: "#FEE2E2",
                  border: "1px solid #FCA5A5",
                  color: "#991B1B",
                  fontSize: "0.875rem",
                  fontFamily: "'Work Sans', sans-serif"
                }}
              >
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={!gradeLevel || !classIdentifier.trim() || !school || loading}
              data-testid="create-class-btn"
              className="primary-btn"
              style={{ opacity: (!gradeLevel || !classIdentifier.trim() || !school || loading) ? 0.6 : 1 }}
            >
              {loading ? "Creating..." : "Create pulse"}
            </button>
          </form>

          <button
            onClick={() => setStep("intro")}
            data-testid="back-btn"
            style={{
              marginTop: "1rem",
              background: "none",
              border: "none",
              color: "#9CA3AF",
              fontSize: "0.875rem",
              cursor: "pointer",
              fontFamily: "'Work Sans', sans-serif"
            }}
          >
            ← Back
          </button>
        </div>
      </div>
    );
  }

  // Success step - show invite link
  if (step === "success") {
    return (
      <div className="pulse-container" data-testid="success-container">
        <div className="pulse-card">
          <div className="pulse-header">
            <h1 className="pulse-title" data-testid="created-class-name">{classData?.name}</h1>
            <p className="pulse-subtitle">Pulse created</p>
          </div>

          <div className="success-message animate-fade-in-up">
            <div className="success-icon">
              <Check size={28} strokeWidth={2.5} color="#15803D" />
            </div>
            <p style={{ color: "#374151", fontWeight: "500", marginBottom: "0.5rem" }}>
              Your class pulse is ready
            </p>
            <p style={{ color: "#6B7280", fontSize: "0.875rem" }}>
              Share the invite link with parents in your class.
            </p>
          </div>

          <div className="invite-link-box" data-testid="invite-link-box">
            <p style={{ fontSize: "0.75rem", color: "#9CA3AF", marginBottom: "0.5rem", textTransform: "uppercase", letterSpacing: "0.05em" }}>
              Class invite link
            </p>
            <div style={{ 
              display: "flex", 
              alignItems: "center", 
              gap: "0.5rem",
              background: "#F9FAFB",
              padding: "0.75rem",
              borderRadius: "0.5rem",
              border: "1px solid #F3F4F6"
            }}>
              <span style={{ 
                flex: 1, 
                fontSize: "0.8125rem", 
                color: "#4B5563",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap"
              }}>
                {getInviteLink()}
              </span>
              <button
                onClick={handleCopy}
                data-testid="copy-link-btn"
                style={{
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  padding: "0.25rem",
                  color: copied ? "#15803D" : "#6B7280"
                }}
              >
                {copied ? <Check size={18} /> : <Copy size={18} />}
              </button>
            </div>
          </div>

          <button
            onClick={handleShareWhatsApp}
            data-testid="share-whatsapp-btn"
            className="whatsapp-btn"
          >
            Share via WhatsApp
          </button>

          <button
            onClick={() => navigate(`/pulse/${classData?.token}`)}
            data-testid="view-pulse-btn"
            className="secondary-btn"
          >
            View your pulse
          </button>
        </div>
      </div>
    );
  }

  return null;
}
